import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  X,
  Calendar,
  Image as ImageIcon,
  FileText,
  Eye,
  ArrowDownToLine,
  FolderOpen,
  CheckCircle2,
  Clock,
  Megaphone
} from "lucide-react";
import FilePreviewLightbox from "../../Desiner/UpcomingEvents/components/FilePreviewLightbox";
import {
  fetchInstitutePrerequisites,
  downloadAllInstitutePrerequisites,
  getFilePresignedViewUrl
} from "../../../services/eventUploadsService";
import { downloadFileFromUrl } from "../../../utils/fileDownloader";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

const SpocPrerequisitesDocuments = ({
  open,
  onClose,
  booking,
  eventType = "fdp"
}) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [prereqData, setPrereqData] = useState({
    bookslot_id: null,
    request_id: null,
    status: null,
    files: []
  });
  const [previewUrls, setPreviewUrls] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  const bookslotId = booking?.bookslot_id || booking?.id;

  useEffect(() => {
    if (!open || !bookslotId) return;

    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchInstitutePrerequisites(bookslotId);
        if (isMounted) {
          setPrereqData(
            data || {
              bookslot_id: bookslotId,
              request_id: null,
              status: null,
              files: []
            }
          );

          // Pre-fetch presigned preview URLs for all active files
          if (data?.files && data.files.length > 0) {
            const urlMap = {};
            await Promise.allSettled(
              data.files.map(async (file) => {
                if (file.key_name) {
                  try {
                    const url = await getFilePresignedViewUrl(file.key_name);
                    if (url) {
                      urlMap[file.id] = url;
                      urlMap[file.key_name] = url;
                    }
                  } catch (e) {
                    console.warn(
                      `Could not pre-fetch view URL for ${file.file_name}:`,
                      e
                    );
                  }
                }
              })
            );
            if (isMounted) {
              setPreviewUrls(urlMap);
            }
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Failed to load prerequisite documents");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [open, bookslotId]);

  if (!booking) return null;

  const files = prereqData.files || [];

  // Categorize files into Banner, Announcement, Standy
  const banners = [];
  const announcement = [];
  const standy = [];

  files.forEach((f) => {
    const title = (f.title || "").toLowerCase();
    const name = (f.file_name || "").toLowerCase();

    if (title.includes("announcement") || name.includes("announcement")) {
      announcement.push(f);
    } else if (
      title.includes("standy") ||
      title.includes("standee") ||
      name.includes("standy") ||
      name.includes("standee")
    ) {
      standy.push(f);
    } else {
      banners.push(f);
    }
  });

  const handlePreview = async (file) => {
    let url = previewUrls[file.id] || previewUrls[file.key_name];
    if (!url && file.key_name) {
      try {
        url = await getFilePresignedViewUrl(file.key_name);
        setPreviewUrls((prev) => ({
          ...prev,
          [file.id]: url,
          [file.key_name]: url
        }));
      } catch (e) {
        console.error("Preview URL fetch failed:", e);
      }
    }

    const isImg = file.file_name?.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i);
    const isPdf = file.file_name?.match(/\.pdf$/i);
    const isDoc = file.file_name?.match(/\.(docx|doc|xls|xlsx|ppt|pptx)$/i);

    setPreviewFile({
      id: file.id,
      name: file.file_name || file.title,
      url: url,
      previewUrl: url,
      type: isImg ? "image" : isPdf ? "pdf" : isDoc ? "docx" : "other"
    });
  };

  const handleDownloadAll = async () => {
    if (!bookslotId || files.length === 0) return;
    setDownloadingAll(true);
    try {
      let downloadFiles = [];
      try {
        const res = await downloadAllInstitutePrerequisites(bookslotId);
        downloadFiles = res?.files || [];
      } catch (err) {
        console.warn("API download-all returned error, falling back to files list:", err);
      }

      // If download-all endpoint didn't return files, use modal files list
      if (!downloadFiles || downloadFiles.length === 0) {
        downloadFiles = files;
      }

      // Sequentially trigger download for each file
      for (let i = 0; i < downloadFiles.length; i++) {
        const item = downloadFiles[i];
        let url = item.presigned_url;
        if (!url && item.key_name) {
          try {
            url = await getFilePresignedViewUrl(item.key_name, true);
          } catch (e) {
            console.error("Error fetching presigned download URL:", e);
          }
        }
        if (url) {
          await downloadFileFromUrl(
            url,
            item.file_name || item.title || `document_${i + 1}`
          );
          if (i < downloadFiles.length - 1) {
            await new Promise((r) => setTimeout(r, 400));
          }
        }
      }
    } catch (err) {
      console.error("Download all failed:", err);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSingle = async (file) => {
    let url = file.presigned_url;
    if (!url && file.key_name) {
      try {
        url = await getFilePresignedViewUrl(file.key_name, true, file.file_name);
      } catch (e) {
        console.error("Error fetching presigned download URL:", e);
      }
    }
    if (!url) {
      url = previewUrls[file.id] || previewUrls[file.key_name];
    }
    if (url) {
      await downloadFileFromUrl(url, file.file_name || file.title || "document");
    }
  };

  const domainTitle =
    booking.domain_name ||
    booking.program_name ||
    booking.institute_name ||
    "Prerequisites Documents";

  const eventTypeLabel =
    eventType === "tech_camp" || eventType === "Tech Camp"
      ? "Tech Camp"
      : "FDP";

  const renderFileCard = (file) => {
    const fileUrl = previewUrls[file.id] || previewUrls[file.key_name];
    const isImg = file.file_name?.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i);

    return (
      <Paper
        key={file.id}
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          transition: "all 0.15s ease",
          "&:hover": {
            borderColor: "#cbd5e1",
            boxShadow: "0 2px 10px rgba(0,0,0,0.04)"
          }
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
          <Box
            onClick={() => handlePreview(file)}
            sx={{
              width: 68,
              height: 54,
              borderRadius: "6px",
              overflow: "hidden",
              backgroundColor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              border: "1px solid #cbd5e1",
              transition: "transform 0.1s ease",
              "&:hover": {
                transform: "scale(1.02)"
              }
            }}
          >
            {isImg && fileUrl ? (
              <img
                src={fileUrl}
                alt={file.file_name || file.title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <FileText size={26} color="#1877f2" />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                mb: 0.25
              }}
              title={file.file_name || file.title}
            >
              {file.file_name || file.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: "0.7rem", display: "block" }}
            >
              {file.title && file.title !== file.file_name
                ? `${file.title} • `
                : ""}
              {formatDate(file.uploaded_at)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            pt: 1,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            gap: 1
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Eye size={13} />}
            onClick={() => handlePreview(file)}
            sx={{
              flex: 1,
              py: 0.45,
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: "6px",
              textTransform: "none",
              color: "#1877f2",
              borderColor: "#bfdbfe",
              backgroundColor: "#eff6ff",
              "&:hover": {
                backgroundColor: "#dbeafe",
                borderColor: "#1877f2"
              }
            }}
          >
            Preview
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<ArrowDownToLine size={13} />}
            onClick={() => handleDownloadSingle(file)}
            sx={{
              flex: 1,
              py: 0.45,
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: "6px",
              textTransform: "none",
              backgroundColor: "#1877f2",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1465cc",
                boxShadow: "none"
              }
            }}
          >
            Download
          </Button>
        </Box>
      </Paper>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            maxHeight: "90vh"
          }
        }}
      >
        {/* Modal Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e2e8f0",
            py: 1.75,
            px: 3,
            backgroundColor: "#f8fafc"
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 0.5,
                flexWrap: "wrap"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                  fontSize: "1.05rem"
                }}
              >
                {domainTitle}
              </Typography>
              <Chip
                label={eventTypeLabel}
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  backgroundColor:
                    eventTypeLabel === "FDP" ? "#eff6ff" : "#f0fdf4",
                  color: eventTypeLabel === "FDP" ? "#1877f2" : "#16a34a",
                  border: `1px solid ${
                    eventTypeLabel === "FDP" ? "#bfdbfe" : "#bbf7d0"
                  }`,
                  borderRadius: "4px"
                }}
              />
              {prereqData.status && (
                <Chip
                  icon={
                    prereqData.status.toLowerCase() === "approved" ? (
                      <CheckCircle2 size={12} color="#16a34a" />
                    ) : (
                      <Clock size={12} color="#d97706" />
                    )
                  }
                  label={
                    prereqData.status.charAt(0).toUpperCase() +
                    prereqData.status.slice(1)
                  }
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "capitalize",
                    backgroundColor:
                      prereqData.status.toLowerCase() === "approved"
                        ? "#f0fdf4"
                        : "#fffbeb",
                    color:
                      prereqData.status.toLowerCase() === "approved"
                        ? "#16a34a"
                        : "#d97706",
                    border: `1px solid ${
                      prereqData.status.toLowerCase() === "approved"
                        ? "#bbf7d0"
                        : "#fde68a"
                    }`,
                    borderRadius: "4px"
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#64748b",
                fontSize: "0.78rem"
              }}
            >
              <Calendar size={13} color="#1877f2" />
              <span>
                {booking.start_date && booking.end_date
                  ? `${booking.start_date} – ${booking.end_date}`
                  : "Event Schedule"}
              </span>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {files.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                disabled={downloadingAll || loading}
                startIcon={
                  downloadingAll ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : (
                    <ArrowDownToLine size={14} />
                  )
                }
                onClick={handleDownloadAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  borderRadius: "6px",
                  borderColor: "#bfdbfe",
                  color: "#1877f2",
                  backgroundColor: "#eff6ff",
                  "&:hover": {
                    backgroundColor: "#dbeafe",
                    borderColor: "#1877f2"
                  }
                }}
              >
                {downloadingAll ? "Downloading..." : "Download All"}
              </Button>
            )}
            <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Modal Body */}
        <DialogContent
          dividers
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            minHeight: "220px",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: "4px"
            }
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                gap: 1.5
              }}
            >
              <CircularProgress size={32} sx={{ color: "#1877f2" }} />
              <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                Loading prerequisite documents...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: "8px" }}>
              {error}
            </Alert>
          ) : files.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                textAlign: "center"
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  color: "#94a3b8"
                }}
              >
                <FolderOpen size={28} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  fontSize: "0.95rem",
                  mb: 0.5
                }}
              >
                No Documents Available
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#94a3b8", fontSize: "0.8rem", maxWidth: 360 }}
              >
                Materials (banner, announcement, standy) for this event have
                not been uploaded yet.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Section 1: Banner */}
              {banners.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "0.92rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5
                    }}
                  >
                    <ImageIcon size={16} color="#1877f2" />
                    Banner ({banners.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(260px, 1fr))"
                      },
                      gap: 1.5
                    }}
                  >
                    {banners.map(renderFileCard)}
                  </Box>
                </Box>
              )}

              {banners.length > 0 && (announcement.length > 0 || standy.length > 0) && (
                <Divider sx={{ borderColor: "#e2e8f0" }} />
              )}

              {/* Section 2: Announcement */}
              {announcement.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "0.92rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5
                    }}
                  >
                    <Megaphone size={16} color="#1877f2" />
                    Announcement ({announcement.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(260px, 1fr))"
                      },
                      gap: 1.5
                    }}
                  >
                    {announcement.map(renderFileCard)}
                  </Box>
                </Box>
              )}

              {announcement.length > 0 && standy.length > 0 && (
                <Divider sx={{ borderColor: "#e2e8f0" }} />
              )}

              {/* Section 3: Standy */}
              {standy.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "0.92rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5
                    }}
                  >
                    <ImageIcon size={16} color="#1877f2" />
                    Standy ({standy.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(260px, 1fr))"
                      },
                      gap: 1.5
                    }}
                  >
                    {standy.map(renderFileCard)}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Modal Footer */}
        <DialogActions
          sx={{
            p: 1.5,
            px: 3,
            borderTop: "1px solid #e2e8f0",
            justifyContent: "flex-end",
            backgroundColor: "#f8fafc"
          }}
        >
          <Button
            onClick={onClose}
            variant="contained"
            size="small"
            sx={{
              px: 2.5,
              py: 0.6,
              borderRadius: "6px",
              fontWeight: 600,
              fontSize: "0.8125rem",
              textTransform: "none",
              backgroundColor: "#1877f2",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#166fe5",
                boxShadow: "none"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox for Zoom / Full Preview */}
      <FilePreviewLightbox
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
        userRole="spoc"
        showDownload={false}
      />
    </>
  );
};

export default SpocPrerequisitesDocuments;
